import { useIntl } from 'react-intl';

import { FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { generalMessages } from 'src/utils/messages';

import type { ControllerRenderProps, FieldValues, Path, UseFormReturn } from 'react-hook-form';

interface FormFieldBooleanSelectProps<T extends FieldValues> {
    form: UseFormReturn<T>;
    name: Path<T>;
    label: { id: string; defaultMessage: string };
    isRequired?: boolean;
}

export function FormFieldBooleanSelect<T extends FieldValues>({
    form,
    name,
    label,
    isRequired = false,
}: FormFieldBooleanSelectProps<T>) {
    const { formatMessage } = useIntl();

    const booleanMapTranslations: Record<string, 'true' | 'false'> = {
        [formatMessage(generalMessages.yes)]: 'true',
        [formatMessage(generalMessages.no)]: 'false',
    };

    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }: { field: ControllerRenderProps<T, Path<T>> }) => (
                <FormItem variant="dialog">
                    <FormLabel variant="dialog" required={isRequired}>
                        {formatMessage(label)}
                    </FormLabel>
                    <FormControlStack>
                        <FormControl>
                            <Select
                                onValueChange={(val: string) => field.onChange(val === 'true')}
                                value={String(field.value ?? '')}
                            >
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder={formatMessage(label)} />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(booleanMapTranslations).map(([key, value]) => (
                                        <SelectItem key={key} value={value}>
                                            {key}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FormControl>
                        <FormMessage />
                    </FormControlStack>
                </FormItem>
            )}
        />
    );
}
